import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { FANVUE_PROFILE_URL } from '../utils/fanvue';
import { trackJoinFanvue } from '../utils/metaPixel';
interface AgeVerificationModalProps {
  isOpen: boolean;
  onClose: () => void;
}
const ACCESS_TRANSITION_MS = 1400;
export function AgeVerificationModal({
  isOpen,
  onClose
}: AgeVerificationModalProps) {
  const {
    t
  } = useLanguage();
  const dialogRef = useRef<HTMLDivElement>(null);
  const checkboxRef = useRef<HTMLInputElement>(null);
  const redirectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [confirmsAge, setConfirmsAge] = useState(false);
  const [confirmationError, setConfirmationError] = useState('');
  const [isEntering, setIsEntering] = useState(false);
  const closeModal = useCallback(() => {
    if (redirectTimerRef.current) {
      clearTimeout(redirectTimerRef.current);
      redirectTimerRef.current = null;
    }
    setConfirmsAge(false);
    setConfirmationError('');
    setIsEntering(false);
    onClose();
  }, [onClose]);
  useEffect(() => {
    if (!isOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const focusTimer = window.setTimeout(() => {
      checkboxRef.current?.focus();
    }, 50);
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !isEntering) {
        event.preventDefault();
        closeModal();
        return;
      }
      if (event.key !== 'Tab' || !dialogRef.current) return;
      const focusableElements = dialogRef.current.querySelectorAll<HTMLElement>('button:not([disabled]), input:not([disabled])');
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      if (!firstElement || !lastElement) return;
      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      window.clearTimeout(focusTimer);
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [closeModal, isEntering, isOpen]);
  if (!isOpen) return null;
  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!confirmsAge) {
      setConfirmationError(t('ageGate.confirmationError'));
      checkboxRef.current?.focus();
      return;
    }
    const accessWindow = window.open('', '_blank');
    if (accessWindow) {
      renderAccessPreparationPage(accessWindow, t('ageGate.enteringTitle'), t('ageGate.enteringMessage'));
    }
    trackJoinFanvue();
    setIsEntering(true);
    redirectTimerRef.current = setTimeout(() => {
      if (accessWindow && !accessWindow.closed) {
        accessWindow.opener = null;
        accessWindow.location.assign(FANVUE_PROFILE_URL);
        return;
      }
      window.location.assign(FANVUE_PROFILE_URL);
    }, ACCESS_TRANSITION_MS);
  };
  return <div className="fixed inset-0 z-[110] flex items-center justify-center overflow-y-auto bg-black/75 px-4 py-6 backdrop-blur-sm" onMouseDown={event => {
    if (!isEntering && event.target === event.currentTarget) closeModal();
  }} data-id="element-9" data-mp="265hn9acvx8zu">
      <div ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby="age-gate-title" aria-describedby="age-gate-description" className="relative w-full max-w-lg overflow-hidden border border-[#C5A028]/45 bg-[#111111] shadow-2xl shadow-black/70" data-id="element-10" data-mp="1diohgdkn1jiz">
        <div className="absolute inset-x-0 top-0 h-px bg-[#C5A028]" data-id="element-11" data-mp="32xctgdpbgzgh" />
        <div className="flex items-start justify-between gap-6 border-b border-white/10 px-6 py-5 sm:px-8" data-id="element-12" data-mp="2ytdehzjoq8p8">
          <div data-id="element-13" data-mp="3u5wg4m6t8n6t">
            <p className="mb-2 text-[10px] font-medium uppercase tracking-[0.24em] text-[#C5A028]" data-id="element-14" data-mp="3u6fyg40dhdld">
              {t('ageGate.eyebrow')}
            </p>
            <h2 id="age-gate-title" className="font-serif text-2xl leading-tight text-white sm:text-3xl" data-id="element-15" data-mp="6yc74iresdvq">
              {t('ageGate.title')}
            </h2>
          </div>
          <button type="button" onClick={closeModal} disabled={isEntering} className="flex h-9 w-9 shrink-0 items-center justify-center border border-white/15 text-xl leading-none text-white/70 transition-colors hover:border-[#C5A028]/60 hover:text-white disabled:cursor-wait disabled:opacity-40" aria-label={t('ageGate.close')} data-id="element-16" data-mp="gwrxgjim3j46">
            ×
          </button>
        </div>

        {isEntering ? <div className="flex min-h-[330px] flex-col items-center justify-center px-6 py-12 text-center sm:px-8" data-id="element-17" data-mp="14mcmnk5czclw">
            <div className="flex h-16 w-16 items-center justify-center rounded-full border border-[#C5A028]/70 bg-[#C5A028]/10" data-id="element-18" data-mp="2p5y795wttybj">
              <span aria-label="Loading" className="h-7 w-7 animate-spin rounded-full border-2 border-[#C5A028]/25 border-t-[#C5A028]" data-id="element-19" data-mp="2k86fcbw6mc7c" />
            </div>
            <h3 className="mt-6 font-serif text-3xl text-white" data-id="element-20" data-mp="2aa0gk35o9m6p">
              {t('ageGate.enteringTitle')}
            </h3>
            <p className="mt-3 text-sm font-light text-white/60" data-id="element-21" data-mp="32wl9h81sq6cd">
              {t('ageGate.enteringMessage')}
            </p>
            <div className="mt-7 h-px w-28 overflow-hidden bg-white/15" data-id="element-22" data-mp="2y53487sqpjll">
              <div className="h-full w-full origin-left bg-[#C5A028] animate-[progress_1.4s_linear_forwards]" data-id="element-23" data-mp="2t71ljnz87man" />
            </div>
          </div> : <form onSubmit={handleSubmit} className="px-6 py-7 sm:px-8 sm:py-8" data-id="element-24" data-mp="3qngtvk3x9z9l">
            <p id="age-gate-description" className="text-sm font-light leading-relaxed text-white/65" data-id="element-25" data-mp="3lunpixcd8sy1">
              {t('ageGate.description')}
            </p>

            <label className="mt-8 flex cursor-pointer items-start gap-3 border-y border-white/10 py-5 text-sm leading-relaxed text-white/70" data-id="element-26" data-mp="1ovot2zi60rtr">
              <input ref={checkboxRef} type="checkbox" checked={confirmsAge} onChange={event => {
            setConfirmsAge(event.target.checked);
            setConfirmationError('');
          }} aria-invalid={Boolean(confirmationError)} aria-describedby={confirmationError ? 'age-gate-confirmation-error' : undefined} className="mt-0.5 h-4 w-4 shrink-0 accent-[#C5A028]" data-id="element-27" data-mp="1f76vss93h2uv" />
              <span data-id="element-28" data-mp="2lok6714o5d9r">{t('ageGate.confirmation')}</span>
            </label>
            {confirmationError && <p id="age-gate-confirmation-error" role="alert" className="mt-2 text-xs text-red-300" data-id="element-29" data-mp="2qlkfrm0q8y5m">
                {confirmationError}
              </p>}

            <button type="submit" className="mt-6 flex w-full items-center justify-center bg-[#C5A028] px-6 py-4 text-xs font-medium uppercase tracking-[0.16em] text-white transition-colors hover:bg-[#D4AF37]" data-id="element-30" data-mp="ootghz4cfqid">
              {t('ageGate.continue')}
            </button>

            <p className="mt-4 text-center text-[11px] leading-relaxed text-white/35" data-id="element-31" data-mp="sqnajok19w9k">
              {t('ageGate.legalNote')}
            </p>
          </form>}
      </div>
    </div>;
}
function renderAccessPreparationPage(accessWindow: Window, title: string, message: string) {
  accessWindow.document.title = title;
  accessWindow.document.body.innerHTML = `
    <main style="min-height:100vh;display:grid;place-items:center;margin:0;background:#0a0a0a;color:#fff;font-family:Arial,sans-serif;text-align:center">
      <section style="width:min(440px,calc(100% - 48px));padding:48px 28px;border:1px solid rgba(197,160,40,.45);background:#111">
        <div style="width:28px;height:28px;margin:0 auto 30px;border:2px solid rgba(197,160,40,.25);border-top-color:#c5a028;border-radius:50%;animation:spin .8s linear infinite"></div>
        <div style="font-family:Georgia,serif;font-size:34px;line-height:1.1">${escapeHtml(title)}</div>
        <p style="margin:14px 0 26px;color:rgba(255,255,255,.62);font-size:15px">${escapeHtml(message)}</p>
        <div style="height:2px;background:rgba(255,255,255,.14);overflow:hidden"><div style="width:100%;height:100%;background:#c5a028;transform-origin:left;animation:progress 1.4s linear forwards"></div></div>
      </section>
    </main>
    <style>@keyframes progress { from { transform: scaleX(0) } to { transform: scaleX(1) } } @keyframes spin { to { transform:rotate(360deg) } }</style>
  `;
}
function escapeHtml(value: string): string {
  return value.replace(/[&<>'"]/g, character => {
    const entities: Record<string, string> = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      "'": '&#39;',
      '"': '&quot;'
    };
    return entities[character];
  });
}